import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { Alert, Box } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { getLegacyWebsiteId } from '../projectConfig';
import { asTenantResolutionError, TenantResolutionError } from './errors';
import { resolveTenantByHostname } from './tenantHostRepository';
import { resolveTenantRuntime } from './resolveTenantRuntime';
import { TenantProvider } from './TenantContext';
import { TenantRuntime } from './types';
import {
  allowsTenantlessControlPlanePath,
  allowsTenantQueryOverride,
  isControlPlaneHostname,
} from './controlPlaneHosts';
import LoadingSpinner from '../components/global/LoadingSpinner';

type TenantBootstrapProps = {
  children: ReactNode;
  explicitTenant?: TenantRuntime;
};

export const TenantBootstrap: React.FC<TenantBootstrapProps> = ({ children, explicitTenant }) => {
  const location = useLocation();
  const [tenant, setTenant] = useState<TenantRuntime | null>(null);
  const [error, setError] = useState<TenantResolutionError | null>(null);
  const tenantQuery = useMemo(
    () => new URLSearchParams(location.search).get('tenant') ?? '',
    [location.search]
  );
  const hostname = window.location.hostname.toLowerCase();
  const intentionallyTenantlessFounderPath = location.pathname.toLowerCase().startsWith('/admin/superadmin') &&
    allowsTenantlessControlPlanePath(location.pathname);
  const shouldPreserveResolvedTenant = Boolean(tenant) && !explicitTenant && !tenantQuery &&
    isControlPlaneHostname(hostname) &&
    !intentionallyTenantlessFounderPath;
  const tenantlessControlPlane = !explicitTenant && !tenantQuery &&
    isControlPlaneHostname(hostname) && allowsTenantlessControlPlanePath(location.pathname) &&
    !shouldPreserveResolvedTenant;

  useEffect(() => {
    if (shouldPreserveResolvedTenant) {
      return;
    }
    if (tenantlessControlPlane) {
      setError(null);
      setTenant(null);
      return;
    }
    let active = true;
    setError(null);
    setTenant(null);
    resolveTenantRuntime({
      explicitTenant,
      hostname: window.location.host,
      search: tenantQuery ? `?tenant=${encodeURIComponent(tenantQuery)}` : '',
      legacyTenantId: getLegacyWebsiteId(),
      hostnameResolutionEnabled: process.env.REACT_APP_ENABLE_RUNTIME_TENANT_HOSTS === 'true',
      localOverrideAllowed: allowsTenantQueryOverride(hostname) && !isControlPlaneHostname(hostname),
      controlPlaneOverrideAllowed: isControlPlaneHostname(hostname),
      resolveHostname: resolveTenantByHostname,
    })
      .then((resolvedTenant) => {
        if (active) setTenant(resolvedTenant);
      })
      .catch((resolutionError: unknown) => {
        if (active) {
          setTenant(null);
          setError(asTenantResolutionError(resolutionError));
        }
      });

    return () => {
      active = false;
    };
  }, [explicitTenant, hostname, shouldPreserveResolvedTenant, tenantQuery, tenantlessControlPlane]);

  if (tenantlessControlPlane) {
    return <>{children}</>;
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2 }}>
        {/* A pending address belongs to a business that hasn't launched yet
            (possibly shown as a preview), so booking pages aren't open. */}
        {error.code === 'PENDING_HOST' ? (
          <Alert severity="info" sx={{ maxWidth: 560 }}>
            Preview only. This business isn't taking bookings yet.
          </Alert>
        ) : (
          <Alert severity="error" sx={{ maxWidth: 560 }}>
            This site could not be resolved ({error.code}).
          </Alert>
        )}
      </Box>
    );
  }

  if (!tenant) {
    return (
      <LoadingSpinner fullScreen />
    );
  }

  return <TenantProvider key={tenant.tenantId} tenant={tenant}>{children}</TenantProvider>;
};
